import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  Divider,
  alpha
} from '@mui/material';
import { 
  Check, 
  Star, 
  TrendingUp, 
  Speed,
  Bolt
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const PricingCard = styled(Card)(({ theme, featured }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'visible',
  background: featured 
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`
    : theme.palette.background.paper,
  border: featured ? `3px solid ${alpha(theme.palette.primary.main, 0.7)}` : `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
  borderRadius: 16,
  transform: featured ? 'scale(1.05)' : 'scale(1)',
  boxShadow: featured ? `0 20px 60px ${alpha(theme.palette.primary.main, 0.22)}` : '0 4px 12px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease-in-out',
  
  '&:hover': {
    transform: featured ? 'scale(1.07)' : 'scale(1.02)',
    boxShadow: featured ? `0 25px 80px ${alpha(theme.palette.primary.main, 0.3)}` : '0 8px 24px rgba(0,0,0,0.12)',
  }
}));

const CTAButton = styled(Button)(({ theme, featured }) => ({
  borderRadius: 12,
  padding: '14px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'none',
  ...(featured && {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.45)}`,
    }
  })
}));

const Pricing = () => {
  const navigate = useNavigate();

  const goToRegisterTracked = (content = 'pricing_cta') => {
    const params = new URLSearchParams({
      utm_source: 'site_facteio',
      utm_medium: 'cta',
      utm_campaign: 'pricing_page',
      utm_content: content
    });
    navigate(`/register?${params.toString()}`);
  };

  const plans = [
    {
      name: 'Free',
      badge: 'Gratuit à vie',
      badgeColor: '#94A3B8',
      price: '0€',
      period: '',
      description: 'Parfait pour tester et les petites activités occasionnelles.',
      timeGain: '1h/mois',
      features: [
        { section: 'Facturer', items: [
          'Jusqu\'à 5 factures par mois',
          'Jusqu\'à 5 devis par mois',
          'Jusqu\'à 3 clients',
          'Suivi statut devis/factures',
          'Personnalisation basique',
          'Compatible facturation électronique 2026',
          'Factures sans TVA (franchise)',
        ]},
        { section: 'Préparer sa comptabilité', items: [
          'Export factures (PDF uniquement)',
          'Réception factures électroniques',
          'Registre des recettes (consultation)',
        ]},
        { section: 'Piloter', items: [
          'Tableaux de bord basiques',
        ]},
        { section: 'Accompagnement', items: [
          'Support communautaire (email)',
        ]},
        { section: 'Limitations', items: [
          '❌ Pas de relances automatiques',
          '❌ Pas de rapprochement bancaire (import CSV)',
          '❌ Pas de notes de frais',
          '❌ Pas de collaboration d\'équipe',
        ]}
      ],
      cta: 'Démarrer gratuitement',
      featured: false
    },
    {
      name: 'Pro',
      badge: 'RECOMMANDÉ',
      badgeColor: '#1F2937',
      price: '14,99€',
      period: '/mois',
      priceSubtext: 'Prix net – TVA non applicable (art. 293 B du CGI)',
      billingNote: 'Offre fondateur : 14,99€ à vie pour les 100 premiers abonnés (prix bloqué).',
      description: 'Pour les pros qui veulent gagner du temps et professionnaliser leur gestion.',
      timeGain: '8h/mois',
      features: [
        { section: 'Facturer', items: [
          'Factures ILLIMITÉES ✨',
          'Devis ILLIMITÉS ✨',
          'Clients ILLIMITÉS ✨',
          'Suivi statut des paiements',
          'Relances automatiques impayés ⚡',
          'Personnalisation (logo, coordonnées, mentions)',
          'Signature électronique des factures',
        ]},
        { section: 'Comptabilité avancée', items: [
          'Export factures (PDF, CSV, Excel)',
          'Rapprochement bancaire via import CSV',
          'Rapprochement factures/transactions',
          'Ajout de factures d\'achat (documents externes)',
          'Registre des recettes complet',
          'Export registre des recettes (CSV)',
          'Notes de frais (workflow + validation)',
          'Export notes de frais (CSV, Excel)',
        ]},
        { section: 'Piloter', items: [
          'Tableaux de bord de suivi',
          'Suivi revenus mensuels',
          'Suivi des dépenses fournisseurs',
          'Statistiques avancées des notes de frais',
        ]},
        { section: 'Collaboration', items: [
          'Accès multi-utilisateurs (3 comptes, propriétaire inclus)',
          'Rôles OWNER / MANAGER / MEMBER',
          'Historique des validations/refus (audit)',
        ]},
        { section: 'Accompagnement Premium', items: [
          'Support email',
          'Mises à jour régulières',
        ]}
      ],
      cta: 'Essayer 30 jours gratuits',
      featured: true
    }
  ];

  return (
    <Box sx={(theme) => ({ background: theme.palette.background.default, minHeight: '100vh', py: 4, overflow: 'visible' })}>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        
        {/* En-tête */}
        <Box textAlign="center" mb={6}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
            <Button variant="text" onClick={() => navigate('/')} sx={{ fontWeight: 600 }}>
              ← Retour à l'accueil
            </Button>
          </Box>
          <Chip 
            label="Prix 25% moins cher que la concurrence" 
            icon={<TrendingUp />}
            sx={{ mb: 3, py: 2.5, fontSize: '1.1rem', fontWeight: 600, bgcolor: '#E5E8EE', color: '#1F2937' }}
          />
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              background: 'linear-gradient(135deg, #1F2937 0%, #A7AFB9 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Des prix justes, des fonctionnalités premium
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.8, fontSize: '1.15rem' }}>
            Commencez gratuitement et pour toujours. Passez à Pro quand vous êtes prêt à gagner encore plus de temps.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '900px', mx: 'auto', mt: 1.5, lineHeight: 1.7 }}>
            Offre fondateur : 14,99€ à vie pour les 100 premiers abonnés (prix bloqué).
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            rowGap={2}
            mt={3}
          >
            <Chip icon={<Check />} label="Sans engagement" sx={{ maxWidth: 200, textAlign: 'center' }} />
            <Chip icon={<Check />} label="Annulation en 1 clic" sx={{ maxWidth: 220, textAlign: 'center' }} />
            <Chip icon={<Check />} label="Données sécurisées" sx={{ maxWidth: 220, textAlign: 'center' }} />
          </Stack>
        </Box>

        {/* Cartes de prix */}
        <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ mt: 3, pt: 3 }}>
          {plans.map((plan, index) => (
            <Grid item xs={12} md={6} lg={5} key={index}>
              <PricingCard featured={plan.featured} sx={{ mt: 2 }}>
                
                {/* Badge en haut */}
                {plan.badge && (
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: -12, 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      zIndex: 10
                    }}
                  >
                    <Chip 
                      label={plan.badge} 
                      sx={(theme) => {
                        const accent = plan.featured ? theme.palette.primary.main : plan.badgeColor;
                        return {
                          bgcolor: accent,
                          color: theme.palette.primary.contrastText,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          height: 28,
                          boxShadow: `0 4px 12px ${alpha(accent, 0.35)}`
                        };
                      }} 
                    />
                  </Box>
                )}

                <CardContent sx={{ p: 3, pt: 4, flexGrow: 1 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    {/* Nom du plan */}
                    <Typography variant="h5" fontWeight={700} mb={1}>
                      {plan.name}
                    </Typography>

                    {/* Prix */}
                    <Box mb={2}>
                      {plan.originalPrice && (
                        <Typography 
                          variant="h6" 
                          color="text.secondary" 
                          sx={{ textDecoration: 'line-through', mb: 0.5 }}
                        >
                          {plan.originalPrice}€
                        </Typography>
                      )}
                      <Box display="flex" alignItems="baseline" justifyContent="center" gap={0.5}>
                        <Typography variant="h3" fontWeight={800} color={plan.featured ? '#1F2937' : 'text.primary'}>
                          {plan.price}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {plan.period}
                        </Typography>
                      </Box>
                      {plan.priceSubtext && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '1rem' }}>
                          {plan.priceSubtext}
                        </Typography>
                      )}
                      {plan.billingNote && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontSize: '0.9rem' }}>
                          {plan.billingNote}
                        </Typography>
                      )}
                      {plan.savings && (
                        <Chip 
                          label={plan.savings} 
                          size="small" 
                          sx={{ mt: 1, bgcolor: '#E5E8EE', color: '#1F2937' }} 
                        />
                      )}
                    </Box>

                    {/* Description */}
                    <Typography variant="body2" color="text.secondary" mb={2} sx={{ minHeight: 40, lineHeight: 1.6, fontSize: '1rem' }}>
                      {plan.description}
                    </Typography>

                    {/* Gain de temps */}
                    <Box 
                      sx={(theme) => {
                        const accent = plan.featured ? theme.palette.primary.main : plan.badgeColor;
                        return {
                          bgcolor: alpha(accent, 0.12),
                          border: `1px solid ${alpha(accent, 0.22)}`,
                          borderRadius: 2,
                          p: 1.5,
                          mb: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          gap: 0.5
                        };
                      }}
                    >
                      <Speed sx={(theme) => ({ color: plan.featured ? theme.palette.primary.main : plan.badgeColor })} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.9rem' }}>
                        En moyenne, vous gagnez
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={(theme) => ({ color: plan.featured ? theme.palette.primary.main : plan.badgeColor })}
                      >
                        {plan.timeGain}
                      </Typography>
                    </Box>

                    {/* Bouton CTA */}
                    <CTAButton 
                      fullWidth 
                      variant={plan.featured ? 'contained' : 'outlined'}
                      featured={plan.featured}
                      size="large"
                      onClick={() => {
                        if (plan.name === 'Pro') {
                          // Si utilisateur connecté, rediriger vers la page d'abonnement
                          const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true' || !!localStorage.getItem('user');
                          if (isLoggedIn) {
                            navigate('/subscription');
                          } else {
                            goToRegisterTracked('pricing_pro');
                          }
                        } else {
                          // Plan Free
                          goToRegisterTracked('pricing_free');
                        }
                      }}
                    >
                      {plan.cta}
                    </CTAButton>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Fonctionnalités */}
                  {plan.features.map((category, idx) => (
                    <Box key={idx} mb={2}>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight={700} 
                        mb={1.5}
                        sx={{ 
                          color: '#2d2d2d',
                          textTransform: 'uppercase',
                          fontSize: '0.85rem',
                          letterSpacing: 1
                        }}
                      >
                        {category.section}
                      </Typography>
                      <List dense disablePadding>
                        {category.items.map((item, itemIdx) => (
                          <ListItem key={itemIdx} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <Check sx={{ fontSize: 18, color: '#94A3B8' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={item}
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: { 
                                  fontSize: '1rem',
                                  fontWeight: item.includes('✨') || item.includes('⚡') || item.includes('✓') ? 600 : 400
                                }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ))}

                </CardContent>
              </PricingCard>
            </Grid>
          ))}
        </Grid>

        {/* Section comparaison */}
        <Box mt={10} textAlign="center">
          <Typography variant="h4" fontWeight={700} mt={6} mb={3}>
            Pourquoi choisir Facteio ?
          </Typography>
          <Grid container spacing={3} mt={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 3 }}>
                <Star sx={{ fontSize: 48, color: '#1F2937', mb: 2, mt: 6 }} />
                <Typography variant="h6" fontWeight={600} mb={1}>
                  Offre Free vraiment gratuite
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                  Pas de carte bancaire, pas de limite de temps. Jusqu'à 5 factures, 5 devis et 3 clients par mois.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 3 }}>
                <Bolt sx={{ fontSize: 48, color: '#1F2937', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} mb={1}>
                  Tarif transparent
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                  Pro à 14,99€ net/mois. TVA non applicable (art. 293 B du CGI). Offre fondateur : 14,99€ à vie pour les 100 premiers abonnés (prix bloqué).
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 3 }}>
                <Speed sx={{ fontSize: 48, color: '#1F2937', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} mb={1}>
                  Conforme 2026
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                  E-invoicing, immutabilité, livre des recettes. 100% prêt pour la loi anti-fraude.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* FAQ */}
        <Box mt={6} p={3} sx={{ bgcolor: '#fff', borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={700} mb={3} textAlign="center">
            Questions fréquentes
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                💳 Dois-je fournir une carte bancaire pour l'offre Free ?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                Non ! L'offre Free ne nécessite aucune carte bancaire. Limitée à 5 factures et 5 devis par mois, parfait pour tester ou les activités occasionnelles.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                📊 Pourquoi passer au plan Pro ?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                Factures/devis ILLIMITÉS, relances automatiques, rapprochement bancaire par import CSV, notes de frais avec workflow d'approbation et exports comptables. Vous gagnez 8h/mois pour 14,99€ net/mois (offre fondateur : 14,99€ à vie pour les 100 premiers abonnés).
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                🔄 Puis-je changer de plan à tout moment ?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                Oui, upgrade ou downgrade en 1 clic. Les changements sont immédiats et vous êtes facturé au prorata.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                ❌ Comment annuler mon abonnement ?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                Depuis vos paramètres, en 1 clic. Pas de justification, pas de frais cachés. Vous gardez l'accès jusqu'à la fin de votre période payée.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                💰 Remboursement possible ?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                Garantie satisfait ou remboursé 30 jours. Si vous n'êtes pas satisfait, nous vous remboursons intégralement, sans question.
              </Typography>
            </Grid>
          </Grid>
        </Box>

      </Container>
    </Box>
  );
};

export default Pricing;
